import React from 'react';
import cx from 'classnames';

import useThemeBlock from '@/hooks/useThemeBlock';
import { Section } from '@/helpers/Section';
import { LinkProps } from '@/helpers/Link/render';
import { ButtonSecondary } from '@/components/atoms/renders';
import { ListHighlightedArtists } from '@/components/renders';
import { getStyles as getSectionStyles } from '@/components/organisms/SectionOneColumn/styles.css';
import { HighlightedArtistType } from '@/components/molecules/ListHighlightedArtists/render';

import block from './block.json';
import { getStyles } from './styles.css';

/**
 * TYPINGS
 */
type SectionHighlightedArtistsProps = SectionOneColumnProps & {
	link?: LinkProps;
	artists?: Array<HighlightedArtistType>;
};

interface ForwardedSectionHighlightedArtists
	extends React.ForwardRefExoticComponent<
			SectionHighlightedArtistsProps & React.RefAttributes<HTMLDivElement>
		>,
		BlockConfigs {}

/**
 * COMPONENT
 */
export const SectionHighlightedArtists = React.forwardRef<
	HTMLDivElement,
	SectionHighlightedArtistsProps
>(
	(
		{
			anchor,
			className,
			title,
			link,
			artists,
			slug,
			variant: currentVariant = 'dark',
			titleTag = 'h2',
			theme = {},
			getStyles: getDfltStyles = getSectionStyles,
		},
		ref
	) => {
		const { variant, blockTheme } = useThemeBlock(
			slug,
			theme,
			currentVariant
		);

		const trimmedSlug = slug.split('/');
		const modifierSlug = trimmedSlug[trimmedSlug.length - 1];

		const rootClass = cx(
			'supt-section supt-section--oneCol',
			`supt-section--${modifierSlug}`,
			'supt-section--colored',
			getDfltStyles(blockTheme),
			getStyles(blockTheme),
			className
		);

		return artists?.length ? (
			<Section
				id={anchor}
				className={rootClass}
				variant={variant}
				slug={slug}
				ref={ref}
			>
				<div className="supt-section__inner">
					{(title || link?.href) && (
						<div className="supt-section__headline">
							{title && (
								<Section.Title
									text={title}
									titleTag={titleTag}
								/>
							)}

							{link?.href ? (
								<div className="supt-section__linkWrapper">
									<ButtonSecondary {...link} />
								</div>
							) : null}
						</div>
					)}
					<div className="supt-section__content">
						<ListHighlightedArtists artists={artists} />
					</div>
				</div>
			</Section>
		) : null;
	}
) as ForwardedSectionHighlightedArtists;

SectionHighlightedArtists.slug = block.slug;
SectionHighlightedArtists.title = block.title;
